import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding, ONBOARDING_STEPS } from '../../context/OnboardingContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FUNDELITE_DESIGN_SYSTEM } from '../../styles/autoStarterDesignSystem';
import {
  getApiBase,
  IMPORT_FAILED_STATUSES,
  IMPORT_SUCCESS_STATUSES,
  isImportReadyForVerification,
  normalizeImportStatusPayload,
  verifyReportBelongsToImport,
} from '../../utils/creditImportStatus';
import { buildProviderImportStartPayload } from '../../utils/providerImportPayload';

import MissingInfoStep, {
  isMissingInfoComplete,
  parseProfileNames,
} from './steps/MissingInfoStep';
import GoalsStep from './steps/GoalsStep';
import UnifiedReportImportStep from './steps/UnifiedReportImportStep';
import ImportLoadingStep from './steps/ImportLoadingStep';

const API = getApiBase();

const TOTAL_STEPS = 3;
const STEP_MISSING_INFO = 1;
const STEP_GOALS = 2;
const STEP_IMPORT = 3;
const STEP_IMPORT_LOADING = 4;

const LOG_PREFIX = '[onboarding-import]';
const IMPORT_SESSION_GRACE_MS = 2500;
const IMPORT_SESSION_TOAST_ID = 'onboarding-import-session';

const REMOVED_BACKEND_STEPS = [
  'social_proof',
  'package_offer',
  'pre_credentials_video',
];

const UI_STEP_TO_BACKEND = {
  [STEP_MISSING_INFO]: 'welcome_terms',
  [STEP_GOALS]: 'goals_selection',
  [STEP_IMPORT]: 'credit_monitoring_credentials',
  [STEP_IMPORT_LOADING]: 'report_import_loading',
};

const STALE_INFO_STEP_PARAMS = new Set(['welcome_terms', 'basic_info', 'welcome_basic_info']);

const getStepSaveErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.detail) return detail.detail;
  return error?.message || 'Could not save your progress. Please try again.';
};

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, updateUser } = useAuth();
  const {
    state: onboardingState,
    loading: onboardingLoading,
    updateStep,
    markDashboardReadyShown,
    completeDashboardEducation,
  } = useOnboarding();

  const [currentStep, setCurrentStep] = useState(STEP_MISSING_INFO);
  const [stepSaving, setStepSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [activeImportId, setActiveImportId] = useState(null);
  const [showImportRestart, setShowImportRestart] = useState(false);
  const pollCleanupRef = useRef(null);
  const pollFailureCountRef = useRef(0);
  const importStartedAtRef = useRef(null);
  const importStartingRef = useRef(false);
  const isMountedRef = useRef(true);
  const activeImportIdRef = useRef(null);
  const earlyDashboardExitRef = useRef(false);
  const awaitingImportCompletionRef = useRef(true);
  const verifiedImportReportIdRef = useRef(null);

  const [formData, setFormData] = useState({
    termsAccepted: false,
    email: user?.email || '',
    firstName: '',
    lastName: '',
    phone: '',
    goals: [],
    selectedPackage: 'complete_credit_repair_builder',
    provider: 'identityiq',
    username: '',
    password: '',
    securityAnswer: '',
  });

  const mapBackendStepToIndex = (backendStep) => {
    const mapping = {
      welcome_terms: STEP_MISSING_INFO,
      basic_info: STEP_MISSING_INFO,
      welcome_basic_info: STEP_MISSING_INFO,
      goals_selection: STEP_GOALS,
      social_proof: STEP_IMPORT,
      package_offer: STEP_IMPORT,
      pre_credentials_video: STEP_IMPORT,
      dispute_workflow_education: STEP_IMPORT,
      dispute_type_selection: STEP_IMPORT,
      credit_monitoring_credentials: STEP_IMPORT,
      report_import_loading: STEP_IMPORT_LOADING,
      dashboard_ready: STEP_IMPORT,
      dashboard_first_use_education: STEP_IMPORT,
      completed: STEP_IMPORT,
    };
    return mapping[backendStep] || STEP_MISSING_INFO;
  };

  const resolveStoredImportId = useCallback(() => {
    return activeImportId || onboardingState?.import_id || null;
  }, [activeImportId, onboardingState?.import_id]);

  useEffect(() => {
    activeImportIdRef.current = activeImportId;
  }, [activeImportId]);

  const isImportSessionActive = useCallback(() => {
    return (
      importStartingRef.current ||
      isLoading ||
      Boolean(activeImportIdRef.current) ||
      Boolean(onboardingState?.import_id)
    );
  }, [isLoading, onboardingState?.import_id]);

  const isValidLoadingResume = useCallback(
    (importId) => {
      if (onboardingState?.import_completed) return false;
      return Boolean(importId);
    },
    [onboardingState?.import_completed]
  );

  const canEnterLoadingStep = useCallback(() => {
    if (onboardingState?.import_completed) return false;
    if (activeImportIdRef.current || onboardingState?.import_id) return true;
    return isImportSessionActive();
  }, [onboardingState?.import_completed, onboardingState?.import_id, isImportSessionActive]);

  const stopImportPolling = useCallback(() => {
    if (pollCleanupRef.current) {
      pollCleanupRef.current();
      pollCleanupRef.current = null;
    }
  }, []);

  const goToDashboardDirectly = useCallback(async ({ reportId } = {}) => {
    const resolvedReportId =
      reportId || verifiedImportReportIdRef.current || onboardingState?.report_id || null;

    if (awaitingImportCompletionRef.current && !resolvedReportId) {
      console.warn(`${LOG_PREFIX} blocked_dashboard_navigation`, {
        reason: 'awaiting_verified_report',
      });
      return;
    }

    stopImportPolling();
    importStartingRef.current = false;
    toast.dismiss(IMPORT_SESSION_TOAST_ID);

    try {
      await updateStep(ONBOARDING_STEPS.DASHBOARD_READY, {}, true);
      if (!isMountedRef.current) return;
      await markDashboardReadyShown();
      if (!isMountedRef.current) return;
      await completeDashboardEducation();
      if (!isMountedRef.current) return;
      updateUser({ onboarding_completed: true, show_dashboard_welcome: true });
      navigate('/dashboard');
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Failed to complete onboarding:', error);
      toast.error('Something went wrong. Please try again.');
    }
  }, [
    updateStep,
    markDashboardReadyShown,
    completeDashboardEducation,
    updateUser,
    navigate,
    stopImportPolling,
    onboardingState?.report_id,
  ]);

  const returnToCredentials = useCallback(
    (reason, message) => {
      if (!isMountedRef.current) return;
      console.log(`${LOG_PREFIX} return_to_credentials`, { reason });
      stopImportPolling();
      pollFailureCountRef.current = 0;
      importStartingRef.current = false;
      earlyDashboardExitRef.current = false;
      awaitingImportCompletionRef.current = true;
      verifiedImportReportIdRef.current = null;
      setActiveImportId(null);
      setImportStatus(null);
      setShowImportRestart(false);
      importStartedAtRef.current = null;
      setIsLoading(false);
      setCurrentStep(STEP_IMPORT);
      navigate('/onboarding?step=credit_monitoring_credentials', { replace: true });
      toast.error(message);
    },
    [navigate, stopImportPolling]
  );

  const goToUiStep = useCallback(
    (uiStep, { replace = true } = {}) => {
      setCurrentStep(uiStep);
      const backendStep = UI_STEP_TO_BACKEND[uiStep] || 'welcome_terms';
      navigate(`/onboarding?step=${backendStep}`, { replace });
    },
    [navigate]
  );

  const applyProfileDefaults = useCallback(
    (prev) => {
      const { firstName, lastName } = parseProfileNames(user, onboardingState);
      return {
        ...prev,
        termsAccepted: onboardingState?.terms_accepted || prev.termsAccepted,
        firstName: firstName || prev.firstName,
        lastName: lastName || prev.lastName,
        email: onboardingState?.email || user?.email || prev.email,
        phone: onboardingState?.phone || user?.phone || user?.phone_number || prev.phone,
        goals: onboardingState?.selected_goals || prev.goals,
        selectedPackage: onboardingState?.selected_package || prev.selectedPackage,
        provider: onboardingState?.selected_provider || prev.provider,
      };
    },
    [user, onboardingState]
  );

  const resolveUiStepIndex = useCallback(
    (backendStep, importId, mergedFormData) => {
      if (onboardingState?.import_completed) {
        return STEP_IMPORT;
      }

      let stepIndex = mapBackendStepToIndex(backendStep);

      if (stepIndex === STEP_IMPORT_LOADING) {
        const resolvedId = importId || activeImportIdRef.current;

        if (canEnterLoadingStep()) {
          if (resolvedId) {
            setActiveImportId(resolvedId);
          }
          console.log(`${LOG_PREFIX} entering_report_import_loading`, {
            importId: resolvedId,
            pendingSync: !resolvedId,
          });
          return STEP_IMPORT_LOADING;
        }

        console.log(`${LOG_PREFIX} loading_step_pending_sync`, {
          backendStep,
          hasImportId: Boolean(resolvedId),
          import_started: onboardingState?.import_started,
          import_completed: onboardingState?.import_completed,
        });
        return STEP_IMPORT;
      }

      if (
        stepIndex === STEP_MISSING_INFO &&
        isMissingInfoComplete(user, onboardingState, mergedFormData)
      ) {
        return STEP_GOALS;
      }

      return stepIndex;
    },
    [
      canEnterLoadingStep,
      onboardingState,
      user,
    ]
  );

  useEffect(() => {
    if (!onboardingState || onboardingLoading || stepSaving) return;

    const merged = applyProfileDefaults(formData);
    setFormData(merged);

    if (
      onboardingState.import_completed &&
      onboardingState.report_id &&
      currentStep === STEP_IMPORT_LOADING &&
      !earlyDashboardExitRef.current
    ) {
      verifiedImportReportIdRef.current = onboardingState.report_id;
      goToDashboardDirectly({ reportId: onboardingState.report_id });
      return;
    }

    const importId = onboardingState.import_id || null;
    const stepIndex = resolveUiStepIndex(
      onboardingState.current_step,
      importId,
      merged
    );

    setCurrentStep((prev) => {
      const target = Math.max(prev, stepIndex);
      if (target > prev) {
        goToUiStep(target);
      }
      return target;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingState, onboardingLoading, user?.email, resolveUiStepIndex, stepSaving, currentStep]);

  useEffect(() => {
    if (stepSaving || onboardingLoading) return;

    const stepParam = searchParams.get('step');
    if (!stepParam) return;

    if (stepParam === 'report_import_loading') {
      if (canEnterLoadingStep()) {
        const importId = resolveStoredImportId();
        if (importId) {
          setActiveImportId(importId);
          console.log(`${LOG_PREFIX} entering_report_import_loading`, { importId, source: 'url' });
        } else {
          console.log(`${LOG_PREFIX} url_loading_pending_sync`);
        }
        goToUiStep(STEP_IMPORT_LOADING);
        return;
      }

      goToUiStep(STEP_IMPORT_LOADING);
      return;
    }

    let normalizedParam = stepParam;
    if (STALE_INFO_STEP_PARAMS.has(stepParam) && isMissingInfoComplete(user, onboardingState, formData)) {
      normalizedParam = 'goals_selection';
    }

    const mapped = mapBackendStepToIndex(normalizedParam);

    if (normalizedParam !== stepParam) {
      goToUiStep(mapped);
      return;
    }

    setCurrentStep((prev) => Math.max(prev, mapped));
  }, [
    searchParams,
    resolveStoredImportId,
    canEnterLoadingStep,
    goToUiStep,
    user,
    onboardingState,
    formData,
    stepSaving,
    onboardingLoading,
    isLoading,
  ]);

  useEffect(() => {
    if (currentStep !== STEP_IMPORT_LOADING) {
      setShowImportRestart(false);
      return undefined;
    }

    const importId = resolveStoredImportId();
    if (importId || importStartingRef.current || isLoading) {
      setShowImportRestart(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      if (!isMountedRef.current) return;
      const retryId = resolveStoredImportId();
      if (retryId || importStartingRef.current || isLoading) {
        setShowImportRestart(false);
        return;
      }
      setShowImportRestart(true);
    }, IMPORT_SESSION_GRACE_MS);

    return () => clearTimeout(timer);
  }, [
    currentStep,
    resolveStoredImportId,
    isLoading,
    activeImportId,
    onboardingState?.import_id,
  ]);

  const handleRestartImport = useCallback(() => {
    setShowImportRestart(false);
    stopImportPolling();
    importStartingRef.current = false;
    setActiveImportId(null);
    setImportStatus(null);
    importStartedAtRef.current = null;
    goToUiStep(STEP_IMPORT);
  }, [goToUiStep, stopImportPolling]);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const markRemovedStepsSkipped = async () => {
    for (const step of REMOVED_BACKEND_STEPS) {
      try {
        await updateStep(step, {}, false, true);
      } catch (error) {
        console.warn(`Failed to mark ${step} skipped`, error);
      }
    }
  };

  const nextStep = async (stepData = {}) => {
    if (stepSaving) return;

    if (!token) {
      toast.error('You must be logged in to continue onboarding.');
      return;
    }

    const activeStep = currentStep;
    const nextUiStep = activeStep < STEP_IMPORT ? activeStep + 1 : activeStep;

    setStepSaving(true);
    try {
      if (activeStep === STEP_MISSING_INFO) {
        const { firstName, lastName } = parseProfileNames(user, onboardingState);
        const payload = {
          first_name: stepData.first_name || formData.firstName || firstName,
          last_name: stepData.last_name || formData.lastName || lastName,
          email: user?.email || onboardingState?.email,
          phone: onboardingState?.phone || undefined,
          terms_accepted: true,
          basic_info_completed: true,
          ...stepData,
        };
        const welcomeResult = await updateStep('welcome_terms', { terms_accepted: true }, true);
        if (!welcomeResult) throw new Error('Could not save terms acceptance.');
        const basicResult = await updateStep('basic_info', payload, true);
        if (!basicResult) throw new Error('Could not save profile details.');
      } else if (activeStep === STEP_GOALS) {
        const goals = stepData.goals || formData.goals || [];
        const goalsResult = await updateStep(
          'goals_selection',
          { goals, selected_goals: goals },
          true
        );
        if (!goalsResult) throw new Error('Could not save your goals.');
        await markRemovedStepsSkipped();
      } else {
        const backendStep = UI_STEP_TO_BACKEND[activeStep] || 'welcome_terms';
        const result = await updateStep(backendStep, stepData, true);
        if (!result) throw new Error('Could not save onboarding step.');
      }

      if (activeStep < STEP_IMPORT) {
        goToUiStep(nextUiStep);
      }
    } catch (error) {
      console.error('Failed to save step:', error);
      toast.error(getStepSaveErrorMessage(error));
    } finally {
      setStepSaving(false);
    }
  };

  const prevStep = () => {
    if (currentStep === STEP_GOALS && isMissingInfoComplete(user, onboardingState, formData)) {
      return;
    }
    if (currentStep > STEP_MISSING_INFO) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const verifyReportForActiveImport = useCallback(
    async (reportId, importId) => {
      if (!reportId || !importId || !token) return { verified: false };

      return verifyReportBelongsToImport({
        reportId,
        importId,
        importStartedAt: importStartedAtRef.current,
        getAuthHeaders: () => ({ Authorization: `Bearer ${token}` }),
      });
    },
    [token]
  );

  const verifyUploadedReportExists = useCallback(
    async (reportId) => {
      if (!reportId || !token) return false;

      try {
        const listResponse = await axios.get(`${API}/report-list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return (listResponse.data?.reports || []).some(
          (report) => report.report_id === reportId
        );
      } catch (error) {
        console.error('Uploaded report check failed:', error);
        return false;
      }
    },
    [token]
  );

  const pollImportStatus = useCallback(
    (importId) => {
      let cancelled = false;
      let pollTimer = null;
      pollFailureCountRef.current = 0;

      const schedulePoll = (delayMs) => {
        if (cancelled) return;
        pollTimer = setTimeout(checkStatus, delayMs);
      };

      const safeSetImportStatus = (value) => {
        if (cancelled || !isMountedRef.current) return;
        setImportStatus(value);
      };

      const checkStatus = async () => {
        if (cancelled || !isMountedRef.current) return;

        try {
          const response = await axios.get(
            `${API}/credit-report/import/${importId}/status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (cancelled || !isMountedRef.current) return;

          pollFailureCountRef.current = 0;
          const data = normalizeImportStatusPayload(response.data);
          safeSetImportStatus(data);

          const { status, report_id: reportId } = data;
          console.log(`${LOG_PREFIX} status poll`, {
            importId,
            status,
            hasReportId: Boolean(reportId),
            progress: data.progress,
          });

          if (isImportReadyForVerification(data) || IMPORT_SUCCESS_STATUSES.has(status)) {
            if (!reportId) {
              schedulePoll(2000);
              return;
            }

            const verification = await verifyReportForActiveImport(reportId, importId);
            if (cancelled || !isMountedRef.current) return;

            if (!verification.verified) {
              if (verification.reason === 'report_predates_import') {
                returnToCredentials(
                  'report_predates_import',
                  'Import could not be verified. Please try again.'
                );
                return;
              }
              schedulePoll(2000);
              return;
            }

            verifiedImportReportIdRef.current = reportId;

            await updateStep(
              ONBOARDING_STEPS.REPORT_IMPORT_LOADING,
              {
                import_id: importId,
                import_status: 'completed',
                import_completed: true,
                report_id: reportId,
              },
              true
            );

            console.log(`${LOG_PREFIX} dashboard_ready`, {
              reason: 'status_completed_and_report_verified',
              importId,
              reportId,
            });

            if (cancelled || !isMountedRef.current) return;
            toast.dismiss(IMPORT_SESSION_TOAST_ID);
            setIsLoading(false);

            if (earlyDashboardExitRef.current) {
              toast.success('Your credit report is ready!');
              return;
            }

            await goToDashboardDirectly({ reportId });
            return;
          }

          if (IMPORT_FAILED_STATUSES.has(status)) {
            if (cancelled || !isMountedRef.current) return;
            await updateStep(ONBOARDING_STEPS.REPORT_IMPORT_LOADING, {
              import_id: importId,
              import_status: 'failed',
              import_completed: false,
            });
            safeSetImportStatus({
              ...data,
              status,
              message: data.error || data.message || 'Import failed. Please try again.',
            });
            setIsLoading(false);
            return;
          }

          schedulePoll(2000);
        } catch (error) {
          if (cancelled || !isMountedRef.current) return;

          pollFailureCountRef.current += 1;
          const httpStatus = error.response?.status;
          console.log(`${LOG_PREFIX} status poll error`, {
            importId,
            httpStatus,
            failures: pollFailureCountRef.current,
          });

          if (!cancelled && isMountedRef.current) {
            setImportStatus((prev) => ({
              ...(prev || {}),
              status: 'running',
              message: 'Still importing...',
            }));
          }

          schedulePoll(3000);
        }
      };

      checkStatus();

      return () => {
        cancelled = true;
        if (pollTimer) clearTimeout(pollTimer);
      };
    },
    [token, updateStep, verifyReportForActiveImport, returnToCredentials, goToDashboardDirectly]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopImportPolling();
    };
  }, [stopImportPolling]);

  useEffect(() => {
    if (onboardingLoading || !token || currentStep !== STEP_IMPORT_LOADING) {
      return undefined;
    }

    const importId = activeImportId || onboardingState?.import_id;
    if (!importId || !isValidLoadingResume(importId)) {
      return undefined;
    }

    if (!activeImportId) {
      setActiveImportId(importId);
    }

    if (pollCleanupRef.current) {
      return undefined;
    }

    pollCleanupRef.current = pollImportStatus(importId);

    return undefined;
  }, [
    onboardingLoading,
    activeImportId,
    onboardingState?.import_id,
    onboardingState?.import_completed,
    onboardingState?.import_started,
    currentStep,
    token,
    pollImportStatus,
    isValidLoadingResume,
  ]);

  const startImport = async () => {
    toast.dismiss(IMPORT_SESSION_TOAST_ID);
    setShowImportRestart(false);
    importStartingRef.current = true;
    setIsLoading(true);

    try {
      await updateStep(ONBOARDING_STEPS.CREDIT_MONITORING_CREDENTIALS, {
        provider: formData.provider,
        submitted: true,
      }, true);

      const importPayload = buildProviderImportStartPayload({
        provider: formData.provider,
        useSavedCredentials: false,
        saveCredentials: formData.saveLogin || formData.saveCredentials || false,
        providerUsername: formData.username,
        providerPassword: formData.password,
        providerSecurityAnswer: formData.securityAnswer,
      });
      console.log(`${LOG_PREFIX} import/start payload`, {
        credential_source: 'manual',
        has_provider_username: !!importPayload.provider_username,
        has_provider_password: !!importPayload.provider_password,
        has_security_answer: !!importPayload.provider_security_answer,
        use_saved_credentials: importPayload.use_saved_credentials,
        save_credentials: importPayload.save_credentials,
      });

      const response = await axios.post(`${API}/credit-report/import/start`, importPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const importId = response?.data?.import_id;
      console.log(`${LOG_PREFIX} import/start response`, {
        httpStatus: response?.status,
        hasImportId: Boolean(importId),
      });

      if (response?.status !== 200 || !importId) {
        throw new Error('Import did not return a valid import_id');
      }

      if (!isMountedRef.current) {
        return { success: false, error: 'Navigation interrupted' };
      }

      setActiveImportId(importId);
      importStartedAtRef.current = new Date().toISOString();
      earlyDashboardExitRef.current = false;
      awaitingImportCompletionRef.current = true;
      verifiedImportReportIdRef.current = null;
      console.log(`${LOG_PREFIX} stored import_id`, { importId });
      toast.dismiss(IMPORT_SESSION_TOAST_ID);

      await updateStep(ONBOARDING_STEPS.REPORT_IMPORT_LOADING, {
        import_id: importId,
        import_status: 'in_progress',
        import_started: true,
        import_completed: false,
      }, true);

      if (!isMountedRef.current) {
        return { success: false, error: 'Navigation interrupted' };
      }

      stopImportPolling();

      setCurrentStep(STEP_IMPORT_LOADING);
      navigate('/onboarding?step=report_import_loading', { replace: true });
      console.log(`${LOG_PREFIX} entering_report_import_loading`, {
        importId,
        source: 'import_start',
      });

      pollCleanupRef.current = pollImportStatus(importId);
      importStartingRef.current = false;

      return { success: true, import_id: importId };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.detail || error.message || 'Import failed. Please try again.';
      console.log(`${LOG_PREFIX} import/start failed`, {
        httpStatus: error.response?.status,
      });

      importStartedAtRef.current = null;
      importStartingRef.current = false;
      returnToCredentials('import_start_failed', message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const ensureImportPolling = useCallback(
    (importId) => {
      if (!importId || !isValidLoadingResume(importId)) return;
      if (pollCleanupRef.current) return;
      pollCleanupRef.current = pollImportStatus(importId);
    },
    [pollImportStatus, isValidLoadingResume]
  );

  const handleKeepWaiting = useCallback(() => {
    earlyDashboardExitRef.current = false;
    awaitingImportCompletionRef.current = true;
    toast.dismiss(IMPORT_SESSION_TOAST_ID);

    const importId = activeImportIdRef.current || onboardingState?.import_id;
    console.log(`${LOG_PREFIX} keep_waiting`, { importId, polling: Boolean(pollCleanupRef.current) });
    ensureImportPolling(importId);
  }, [ensureImportPolling, onboardingState?.import_id]);

  const handleContinueWhileImporting = async () => {
    earlyDashboardExitRef.current = true;
    awaitingImportCompletionRef.current = false;
    importStartingRef.current = false;

    try {
      await markDashboardReadyShown();
      await completeDashboardEducation();
      updateUser({
        onboarding_completed: true,
        show_dashboard_welcome: true,
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Continue while importing failed:', error);
      toast.error('Could not continue to dashboard. Please try again.');
    }
  };

  const handleImportLater = async () => {
    try {
      console.log(`${LOG_PREFIX} import_later`);

      stopImportPolling();
      importStartingRef.current = false;
      setActiveImportId(null);
      setImportStatus(null);
      setShowImportRestart(false);
      setIsLoading(false);

      await updateStep(
        ONBOARDING_STEPS.REPORT_IMPORT_LOADING,
        {
          import_completed: false,
          import_started: false,
          import_id: null,
          import_status: 'skipped',
        },
        false,
        true
      );

      if (!isMountedRef.current) return;

      await markDashboardReadyShown();
      await completeDashboardEducation();

      updateUser({
        onboarding_completed: true,
        show_dashboard_welcome: true,
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Import later failed:', error);
      toast.error('Could not continue to dashboard. Please try again.');
    }
  };

  const renderStep = () => {
    const stepProps = {
      formData,
      updateFormData,
      nextStep,
      prevStep,
      currentStep,
      totalSteps: TOTAL_STEPS,
      designSystem: FUNDELITE_DESIGN_SYSTEM,
      user,
      onboardingState,
    };

    switch (currentStep) {
      case STEP_MISSING_INFO:
        return <MissingInfoStep {...stepProps} />;
      case STEP_GOALS:
        return (
          <GoalsStep
            {...stepProps}
            showBack={!isMissingInfoComplete(user, onboardingState, formData)}
            saving={stepSaving}
            onContinue={nextStep}
          />
        );
      case STEP_IMPORT:
        return (
          <UnifiedReportImportStep
            {...stepProps}
            onStartImport={async () => {
              const result = await startImport();
              return result;
            }}
            onManualUpload={async (file) => {
              const formDataUpload = new FormData();
              formDataUpload.append('file', file);
              formDataUpload.append('provider', 'manual');

              try {
                const response = await axios.post(`${API}/credit-report/upload`, formDataUpload, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                  },
                });

                const reportId = response.data.report_id;
                if ((response.data.success || reportId) && reportId) {
                  const reportReady = await verifyUploadedReportExists(reportId);
                  if (!reportReady) {
                    throw new Error('Uploaded report could not be verified');
                  }
                  await updateStep(
                    ONBOARDING_STEPS.REPORT_IMPORT_LOADING,
                    {
                      import_status: 'completed',
                      import_completed: true,
                      report_id: reportId,
                    },
                    true
                  );
                  await goToDashboardDirectly({ reportId });
                  return { success: true, report_id: reportId };
                }
                throw new Error(response.data.error || 'Upload failed');
              } catch (error) {
                toast.error(error.response?.data?.detail || error.message || 'Upload failed');
                throw error;
              }
            }}
            onImportLater={handleImportLater}
            onSkipToHome={handleImportLater}
          />
        );
      case STEP_IMPORT_LOADING:
        return (
          <ImportLoadingStep
            importStatus={importStatus}
            importStartedAt={importStartedAtRef.current}
            onContinueToDashboard={handleContinueWhileImporting}
            onKeepWaiting={handleKeepWaiting}
            showSessionRestart={showImportRestart}
            onRestartImport={handleRestartImport}
          />
        );
      default:
        return <MissingInfoStep {...stepProps} />;
    }
  };

  if (onboardingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: FUNDELITE_DESIGN_SYSTEM.backgrounds.app }}>
        <div
          className="animate-spin w-8 h-8 border-4 rounded-full"
          style={{
            borderColor: FUNDELITE_DESIGN_SYSTEM.borders.light,
            borderTopColor: FUNDELITE_DESIGN_SYSTEM.brand.green500,
          }}
        />
      </div>
    );
  }

  const showProgressChrome = currentStep <= STEP_IMPORT;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: `linear-gradient(135deg, ${FUNDELITE_DESIGN_SYSTEM.brand.green800} 0%, ${FUNDELITE_DESIGN_SYSTEM.brand.green700} 50%, ${FUNDELITE_DESIGN_SYSTEM.brand.green600} 100%)`,
      }}
    >
      {showProgressChrome && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-1" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
            <motion.div
              className="h-full"
              style={{ backgroundColor: FUNDELITE_DESIGN_SYSTEM.mint[200] }}
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-2xl"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {showProgressChrome && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2">
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            {[...Array(TOTAL_STEPS)].map((_, i) => (
              <div
                key={i}
                className="transition-all duration-300"
                style={{
                  width: i + 1 === currentStep ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor:
                    i + 1 <= currentStep
                      ? FUNDELITE_DESIGN_SYSTEM.mint[200]
                      : 'rgba(255,255,255,0.3)',
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingFlow;
